import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Info as InfoIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  HourglassEmpty as PendingIcon,
  AccessTime as TimeIcon,
  History as HistoryIcon,
  Policy as PolicyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SupervisorAccount as AdminIcon,
  Person as OwnerIcon,
} from '@mui/icons-material';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../animations/variants';
import { getApproverRequestHistory, getRequest } from '../../services/jitApi';
import { getJITUser } from '../../services/jitApi';
import { hasMinRole, ROLES } from '../../utils/roles';

const JITRequestHistory = () => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery('(max-width:767px)');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [showPolicies, setShowPolicies] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25, total: 0, totalPages: 0 });

  const jitUser = getJITUser();
  const isSSOAdmin = hasMinRole(jitUser?.role, ROLES.SSO_ADMIN);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getApproverRequestHistory({ status: statusFilter, page: pagination.page + 1, pageSize: pagination.pageSize });
      setRequests(response.data?.requests || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data?.total || 0,
        totalPages: response.data?.totalPages || 0,
      }));
    } catch (err) {
      console.error('Failed to fetch request history:', err);
      setError('Failed to load request history');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleViewDetails = async (requestId) => {
    try {
      const response = await getRequest(requestId);
      setSelectedRequest(response.data);
      setDetailsOpen(true);
      setShowPolicies(false);
    } catch (err) {
      console.error('Failed to fetch request details:', err);
    }
  };

  const getStatusChip = (status, isCustomPermissionSet = false) => {
    switch (status) {
      case 'pending_owner':
        return (
          <Chip
            size="small"
            label="Awaiting Owner"
            icon={<OwnerIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              backgroundColor: 'rgba(255, 152, 0, 0.15)',
              color: '#E65100',
              fontWeight: 500,
            }}
          />
        );
      case 'pending_admin':
        return (
          <Chip
            size="small"
            label="Awaiting Admin"
            icon={<AdminIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              backgroundColor: 'rgba(70, 152, 211, 0.15)',
              color: '#1565C0',
              fontWeight: 500,
            }}
          />
        );
      case 'pending':
        return (
          <Chip
            size="small"
            label="Pending"
            icon={<PendingIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              backgroundColor: 'rgba(156, 39, 176, 0.15)',
              color: '#7B1FA2',
              fontWeight: 500,
            }}
          />
        );
      case 'approved':
        return (
          <Chip
            size="small"
            label="Approved"
            icon={<ApprovedIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              backgroundColor: 'rgba(39, 174, 96, 0.15)',
              color: '#27AE60',
              fontWeight: 500,
            }}
          />
        );
      case 'rejected':
        return (
          <Chip
            size="small"
            label="Rejected"
            icon={<RejectedIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              backgroundColor: 'rgba(231, 76, 60, 0.15)',
              color: '#E74C3C',
              fontWeight: 500,
            }}
          />
        );
      case 'expired':
        return (
          <Chip
            size="small"
            label="Expired"
            icon={<TimeIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              backgroundColor: 'rgba(158, 158, 158, 0.15)',
              color: '#757575',
              fontWeight: 500,
            }}
          />
        );
      case 'revoked':
        return (
          <Chip
            size="small"
            label="Revoked"
            icon={<RejectedIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              backgroundColor: 'rgba(231, 76, 60, 0.15)',
              color: '#E74C3C',
              fontWeight: 500,
            }}
          />
        );
      default:
        return (
          <Chip
            size="small"
            label={status}
            sx={{ fontFamily: 'Metropolis, sans-serif' }}
          />
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderApprovalTimeline = (request) => {
    if (!request.isCustomPermissionSet) return null;

    const steps = [
      {
        label: 'Request Submitted',
        completed: true,
        description: formatDate(request.createdAt),
      },
      {
        label: 'Owner Approval',
        completed: !!request.ownerApprovedAt,
        description: request.ownerApprovedAt
          ? `${request.ownerApproverEmail} on ${formatDate(request.ownerApprovedAt)}`
          : 'Pending',
      },
      {
        label: 'Admin Approval',
        completed: !!request.adminApprovedAt,
        description: request.adminApprovedAt
          ? `${request.adminApproverEmail} on ${formatDate(request.adminApprovedAt)}`
          : 'Pending',
      },
    ];

    return (
      <Box sx={{ mt: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            color: '#253E66',
            mb: 2,
          }}
        >
          Approval Progress
        </Typography>
        <Stepper orientation="vertical" activeStep={steps.findIndex(s => !s.completed)}>
          {steps.map((step) => (
            <Step key={step.label} completed={step.completed}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    color: step.completed ? '#27AE60' : undefined,
                    '&.Mui-completed': { color: '#27AE60' },
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: step.completed ? 600 : 400,
                  }}
                >
                  {step.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    color: '#666666',
                  }}
                >
                  {step.description}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    );
  };

  const renderCustomPSDetails = (request) => {
    if (!request.isCustomPermissionSet) return null;

    const managedPolicies = request.customManagedPolicies || [];
    const inlinePolicies = request.customInlinePolicies || {};

    return (
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            mb: 1,
          }}
          onClick={() => setShowPolicies(!showPolicies)}
        >
          {showPolicies ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              color: '#253E66',
              ml: 1,
            }}
          >
            Custom Permission Set Details
          </Typography>
          <Chip
            size="small"
            icon={<PolicyIcon sx={{ fontSize: 12 }} />}
            label="Custom"
            sx={{
              ml: 1,
              fontFamily: 'Metropolis, sans-serif',
              backgroundColor: 'rgba(255, 152, 0, 0.15)',
              color: '#E65100',
              height: 22,
            }}
          />
        </Box>

        <Collapse in={showPolicies}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: 'rgba(255, 152, 0, 0.04)',
              border: '1px solid rgba(255, 152, 0, 0.15)',
              borderRadius: '6px',
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ fontFamily: 'Metropolis, sans-serif' }}
              >
                Permission Set Name
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}
              >
                JIT-{request.customPermissionSetName}
              </Typography>
            </Box>

            {managedPolicies.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ fontFamily: 'Metropolis, sans-serif' }}
                >
                  AWS Managed Policies ({managedPolicies.length})
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {managedPolicies.map((arn, idx) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: '#333',
                        backgroundColor: '#f5f5f5',
                        p: 0.5,
                        borderRadius: '4px',
                        mb: 0.5,
                        wordBreak: 'break-all',
                      }}
                    >
                      {arn}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {Object.keys(inlinePolicies).length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ fontFamily: 'Metropolis, sans-serif' }}
                >
                  Inline Policies ({Object.keys(inlinePolicies).length})
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {Object.entries(inlinePolicies).map(([name], idx) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 500,
                        color: '#333',
                        mb: 0.5,
                      }}
                    >
                      {name}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        </Collapse>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#4698D3' }} />
      </Box>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Header */}
      <motion.div variants={sectionVariants}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: { xs: 2, sm: 0 }, mb: { xs: 3, sm: 4 } }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Bitter, Georgia, serif',
              fontWeight: 600,
              color: '#253E66',
              mb: 1,
            }}
          >
            Request History
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
            }}
          >
            {isSSOAdmin
              ? 'View all access requests across your organization'
              : 'View all access requests for accounts you own'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 } }}>
            <InputLabel sx={{ fontFamily: 'Metropolis, sans-serif' }}>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => { setStatusFilter(e.target.value); setPagination((prev) => ({ ...prev, page: 0 })); }}
              sx={{ fontFamily: 'Metropolis, sans-serif' }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="pending_owner">Awaiting Owner</MenuItem>
              <MenuItem value="pending_admin">Awaiting Admin</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="revoked">Revoked</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton
              onClick={fetchRequests}
              sx={{
                border: '1px solid #E5E5E5',
                borderRadius: '6px',
                '&:hover': {
                  borderColor: '#4698D3',
                  backgroundColor: 'rgba(70, 152, 211, 0.04)',
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      </motion.div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <motion.div variants={sectionVariants}>
      {requests.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: 'rgba(70, 152, 211, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 2,
            }}
          >
            <HistoryIcon sx={{ fontSize: 32, color: '#4698D3' }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              color: '#253E66',
              mb: 1,
            }}
          >
            No Request History
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
            }}
          >
            {statusFilter
              ? `No requests found with status "${statusFilter}"`
              : 'No access requests found for your accounts'}
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {requests.map((request, index) => (
            <Paper
              key={request.id}
              elevation={0}
              onClick={() => handleViewDetails(request.id)}
              sx={{
                '@keyframes fadeInItem': {
                  from: { opacity: 0, transform: 'translateY(8px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                animation: `fadeInItem 0.3s ease-out ${index * 0.05}s both`,
                p: 2,
                border: '1px solid #E5E5E5',
                borderRadius: '8px',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#4698D3',
                  backgroundColor: 'rgba(70, 152, 211, 0.02)',
                },
                '&:active': {
                  backgroundColor: 'rgba(70, 152, 211, 0.06)',
                },
              }}
            >
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  color: '#253E66',
                  mb: 1.5,
                }}
              >
                {request.requesterEmail}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                <Typography
                  variant="caption"
                  sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', flexShrink: 0 }}
                >
                  Account
                </Typography>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                    color: '#333333',
                    ml: 2,
                    textAlign: 'right',
                  }}
                >
                  {request.accountName || request.accountId}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', flexShrink: 0 }}
                >
                  Permission Set
                </Typography>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    color: '#333333',
                    ml: 2,
                    textAlign: 'right',
                  }}
                >
                  {request.isCustomPermissionSet
                    ? `JIT-${request.customPermissionSetName}`
                    : (request.permissionSetName || request.permissionSetId)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pt: 1.5,
                  borderTop: '1px solid #F0F0F0',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  {getStatusChip(request.status, request.isCustomPermissionSet)}
                  <Chip
                    size="small"
                    icon={<TimeIcon sx={{ fontSize: 14 }} />}
                    label={`${request.requestedDuration}h`}
                    sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}
                  />
                </Box>
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{ display: 'flex', gap: 0.5 }}
                >
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => handleViewDetails(request.id)}
                      sx={{
                        color: '#4698D3',
                        '&:hover': {
                          backgroundColor: 'rgba(70, 152, 211, 0.08)',
                        },
                      }}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          ))}
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            onPageChange={(e, newPage) => setPagination((prev) => ({ ...prev, page: newPage }))}
            rowsPerPage={pagination.pageSize}
            onRowsPerPageChange={(e) => setPagination((prev) => ({ ...prev, page: 0, pageSize: parseInt(e.target.value, 10) }))}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid #E5E5E5',
              fontFamily: 'Metropolis, sans-serif',
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontFamily: 'Metropolis, sans-serif',
              },
            }}
          />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            overflowX: 'auto',
          }}
        >
          <Table sx={{ minWidth: { xs: 450, sm: 700 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Requester
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Account
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Permission Set
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Duration
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Requested
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request, index) => (
                <TableRow
                  key={request.id}
                  hover
                  sx={{
                    '@keyframes fadeInRow': {
                      from: { opacity: 0, transform: 'translateY(8px)' },
                      to: { opacity: 1, transform: 'translateY(0)' },
                    },
                    animation: `fadeInRow 0.3s ease-out ${index * 0.03}s both`,
                    '&:hover': {
                      backgroundColor: 'rgba(70, 152, 211, 0.02)',
                    },
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        color: '#253E66',
                      }}
                    >
                      {request.requesterEmail}
                    </Typography>
                    {request.requesterName && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#666666',
                          fontFamily: 'Metropolis, sans-serif',
                        }}
                      >
                        {request.requesterName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 500,
                        color: '#333333',
                      }}
                    >
                      {request.accountName || request.accountId}
                    </Typography>
                    {request.accountName && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#666666',
                          fontFamily: 'Metropolis, sans-serif',
                        }}
                      >
                        {request.accountId}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          color: '#333333',
                        }}
                      >
                        {request.isCustomPermissionSet
                          ? `JIT-${request.customPermissionSetName}`
                          : (request.permissionSetName || request.permissionSetId)}
                      </Typography>
                      {request.isCustomPermissionSet && (
                        <Chip
                          size="small"
                          label="Custom"
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            backgroundColor: 'rgba(255, 152, 0, 0.15)',
                            color: '#E65100',
                            height: 20,
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(request.status, request.isCustomPermissionSet)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      icon={<TimeIcon sx={{ fontSize: 14 }} />}
                      label={`${request.requestedDuration}h`}
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        color: '#666666',
                      }}
                    >
                      {formatDate(request.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(request.id)}
                        sx={{
                          color: '#4698D3',
                          '&:hover': {
                            backgroundColor: 'rgba(70, 152, 211, 0.08)',
                          },
                        }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            onPageChange={(e, newPage) => setPagination((prev) => ({ ...prev, page: newPage }))}
            rowsPerPage={pagination.pageSize}
            onRowsPerPageChange={(e) => setPagination((prev) => ({ ...prev, page: 0, pageSize: parseInt(e.target.value, 10) }))}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid #E5E5E5',
              fontFamily: 'Metropolis, sans-serif',
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontFamily: 'Metropolis, sans-serif',
              },
            }}
          />
        </TableContainer>
      )}
      </motion.div>

      {/* Request Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
          Request Details
        </DialogTitle>
        <DialogContent dividers>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          {selectedRequest && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 4 } }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    Requester
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, wordBreak: 'break-word' }}>
                    {selectedRequest.requesterEmail}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {getStatusChip(selectedRequest.status, selectedRequest.isCustomPermissionSet)}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 4 } }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    AWS Account
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                    {selectedRequest.accountName || selectedRequest.accountId}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    Duration
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                    {selectedRequest.requestedDuration} hours
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                  Permission Set
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                  {selectedRequest.isCustomPermissionSet
                    ? `JIT-${selectedRequest.customPermissionSetName}`
                    : (selectedRequest.permissionSetName || selectedRequest.permissionSetId)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                  Justification
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mt: 0.5,
                    backgroundColor: '#F9FBFD',
                    border: '1px solid #E5E5E5',
                    borderRadius: '6px',
                  }}
                >
                  <Typography variant="body2" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    {selectedRequest.justification || 'No justification provided'}
                  </Typography>
                </Paper>
              </Box>

              {selectedRequest.reviewerEmail && (
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    Reviewed By
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                    {selectedRequest.reviewerEmail}
                    {selectedRequest.reviewedAt && ` on ${formatDate(selectedRequest.reviewedAt)}`}
                  </Typography>
                  {selectedRequest.reviewerComment && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        mt: 1,
                        backgroundColor: '#F9FBFD',
                        border: '1px solid #E5E5E5',
                        borderRadius: '6px',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                        {selectedRequest.reviewerComment}
                      </Typography>
                    </Paper>
                  )}
                </Box>
              )}

              {renderCustomPSDetails(selectedRequest)}
              {renderApprovalTimeline(selectedRequest)}
            </Box>
          )}
          </motion.div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)} sx={{ fontFamily: 'Metropolis, sans-serif' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default JITRequestHistory;
